import { t } from 'i18next';

import type APIAddress from '~/types/APIAddress';
import type Address from '~/types/Address';
import type CustomsItem from '~/types/CustomsItem';
import type LineItem from '~/types/LineItem';
import type Order from '~/types/Order';
import type Rate from '~/types/Rate';

import { currencies } from '~/constants/currency';
import { getVerificationStatus } from '~/services/user.service';
import { requiresCustomsDeclaration as isCustomsRequired } from '~/types/CustomsDeclaration';
import { IDVerificationStatus } from '~/types/IDVerification';
import { type OrderSelection } from '~/types/Order';
import { getTransactionFor, type BatchEntryExtended } from '~/types/OrderBatchEntry';
import { type Parcel } from '~/types/Parcel';
import { TransactionStatus } from '~/types/Transaction';
import { isRowEntryFulfilled } from '~/utils/batchEntryRendering';
import { type TranslationFunction } from '~/utils/translate';

export const batchEntriesBuyMessage = (
  count: number,
  t: TranslationFunction,
  showNext?: boolean,
  isReturnLabel?: boolean,
) => {
  if (isReturnLabel) return t('orders:drawers:buyLabel:createReturnLabel');

  if (count === 0) return t('orders:drawers:buyLabel:buyLabel');

  return showNext && count === 1
    ? t('orders:phrases:next')
    : t('orders:drawers:buyLabel:buyLabelWithCount', { count });
};

export const batchEntriesOrdersCountMessage = (
  selectedOrdersCount: number,
  batchEntriesToBuyCount: number,
  t: TranslationFunction,
) => {
  if (selectedOrdersCount === 0) return '';

  return t('orders:actions:multi:readyToBuy', {
    count: batchEntriesToBuyCount,
    total: selectedOrdersCount,
  });
};

export const getTruncatedOrderNumber = (orderNumber: string, maxLength = 25): string => {
  if (orderNumber.length > maxLength) return `...${orderNumber.slice(-maxLength)}`;

  return orderNumber;
};

export const singleOrderMessage = (
  t: TranslationFunction,
  orderNumber: string,
  isReturnLabel = false,
) => {
  if (!orderNumber) return;

  const truncatedOrderNumber = getTruncatedOrderNumber(orderNumber);

  if (isReturnLabel) return `${t('orders:drawers:buyLabel:forOrder')} ${truncatedOrderNumber}`;

  return truncatedOrderNumber;
};

export const getDrawerTitle = (
  t: TranslationFunction,
  selectionLength: number,
  isReturnLabel = false,
) => {
  if (isReturnLabel) return t('orders:all:createReturn');

  return selectionLength <= 1
    ? t('orders:drawers:buyLabel:title')
    : t('orders:drawers:buyLabel:titleMultiple');
};

export const getSelectedBatchEntriesById = (
  batchEntryIds: number[],
  batchEntryData: HashMap<number, BatchEntryExtended>,
) => batchEntryIds.map((id) => batchEntryData[id]).filter((be) => !!be);

export const getSelectedFulfilledOrders = (
  batchEntryIds: number[],
  ordersData: Order[],
  batchEntryData: HashMap<number, BatchEntryExtended>,
) => {
  const ordersDataHashmap = ordersData.reduce<Record<string, Order | undefined>>((accum, curr) => {
    accum[curr.objectId] = curr;
    return accum;
  }, {});

  const selectedBatchEntries = getSelectedBatchEntriesById(batchEntryIds, batchEntryData);

  return selectedBatchEntries.filter((batchEntry) => {
    const order = ordersDataHashmap[batchEntry.orderObjectId];
    if (!order || batchEntry.isReturn) return false;
    if (!isRowEntryFulfilled(order, false, batchEntry)) return false;

    const transaction = getTransactionFor(order, batchEntry);
    if (transaction && transaction.status !== TransactionStatus.SUCCESS) return false;

    return true;
  });
};

export const shouldShowCustomsCard = ({
  isBypassCustoms,
  orderRequiresCustoms,
}: {
  isBypassCustoms: boolean;
  orderRequiresCustoms: boolean;
}) => orderRequiresCustoms && !isBypassCustoms;

export const singleOrMultiSelectionShouldShowCustoms = (
  batchEntries: BatchEntryExtended[],
  ordersData: { [orderId: string]: Order },
  selectedOrders: OrderSelection,
  defaultUserAddress?: APIAddress,
): boolean => {
  for (const [orderId, batchEntryIds] of Object.entries(selectedOrders)) {
    // If value is not an empty array then this order has batchEntry
    // We ignore the order in this section
    if (batchEntryIds.length) continue;

    // Find the order from the hash
    const order = ordersData[orderId];

    if (order) {
      const fromAddress = order.addressFrom || defaultUserAddress;
      if (order.toAddress && fromAddress && isCustomsRequired(order.toAddress, fromAddress))
        return true;
    }
  }

  for (const entry of batchEntries) {
    if (
      entry.addressFrom &&
      entry.addressTo &&
      isCustomsRequired(entry.addressTo, entry.addressFrom)
    )
      return true;
  }

  return false;
};

export const isEelPfcRequired = ({
  batchEntries,
  defaultUserAddress,
  ordersData,
  selectedOrders,
  selectedRates,
}: {
  batchEntries: BatchEntryExtended[];
  defaultUserAddress?: APIAddress;
  ordersData: { [orderId: string]: Order };
  selectedOrders: OrderSelection;
  selectedRates: Maybe<Rate>[];
}) => {
  if (
    !singleOrMultiSelectionShouldShowCustoms(
      batchEntries,
      ordersData,
      selectedOrders,
      defaultUserAddress,
    )
  )
    return false;

  return batchEntries.some((batchEntry) => {
    const selectedRate = selectedRates.find(
      (rate) => rate?.shipment === batchEntry.shipmentObjectId,
    );

    if (selectedRate?.provider !== 'USPS') return false;
    if (batchEntry.customsDeclaration?.eelPfc) return false;
    if (batchEntry.customsDeclaration?.aesItn) return false;
    return true;
  });
};

export const mapCustomsItemToLineItem = (item: CustomsItem): LineItem => {
  const {
    description,
    id,
    originCountry,
    originalMassUnit,
    originalNetWeight,
    originalUnitWeight,
    quantity,
    skuCode,
    unitValue,
    valueAmount,
  } = item;

  return {
    appId: '',
    countryOfOrigin: originCountry,
    currency: currencies.USD,
    hsCode: undefined,
    id,
    isFulfilled: false,
    manufactureCountry: originCountry.name,
    maxDeliveryTime: undefined,
    maxShipTime: undefined,
    objectId: '',
    product: description,
    quantity,
    sku: skuCode || '',
    title: description,
    totalAmount: valueAmount.toString(),
    totalWeight: originalNetWeight,
    unitAmount: unitValue.toString(),
    unitWeight: originalUnitWeight,
    variant: '',
    variantTitle: '',
    weightUnit: originalMassUnit,
  };
};

export const mapCustomsItemsToLineItems = (items?: CustomsItem[] | null): LineItem[] => {
  if (!items) return [];

  return items.map(mapCustomsItemToLineItem);
};

export const getLineItemsFromSelectionOrCustoms = (
  lineItems: LineItem[],
  customItems: CustomsItem[],
  shouldShowCustoms?: boolean,
): LineItem[] => (shouldShowCustoms ? mapCustomsItemsToLineItems(customItems) : lineItems);

export const getToAddress = (batchEntry?: BatchEntryExtended, order?: Order): Address | null =>
  batchEntry?.addressTo || order?.toAddress || null;

export const getParcel = (batchEntry?: BatchEntryExtended, order?: Order): Parcel | undefined =>
  (batchEntry || order)?.parcel || undefined;

type GetDrawerSubtitleArgs = {
  isReturnLabel: boolean;
  orderNumber?: string;
  successfulOrdersCount: number;
};

export const getPostPurchaseDrawerSubtitle = ({
  isReturnLabel,
  orderNumber,
  successfulOrdersCount,
}: GetDrawerSubtitleArgs) => {
  if (successfulOrdersCount === 1 && isReturnLabel) {
    return orderNumber
      ? `${t(
          'orders:blackbeardDownloadLabels.returnLabelOrderNumberPrefix',
        )} ${getTruncatedOrderNumber(orderNumber)}`
      : '';
  }

  return t('orders:blackbeardDownloadLabels.downloadLabelsSubtitle', {
    count: successfulOrdersCount,
  });
};

export const isVerificationProcessing = async () => {
  try {
    const res = await getVerificationStatus();

    return res.data.state === IDVerificationStatus.VERIFICATION_PROCESSING;
  } catch (err) {
    return false;
  }
};
